// src/config/branding.js
// Centralized branding configuration - Single source of truth for all brand elements

export const BRAND_CONFIG = {
  // Core brand elements
  name: 'Life Re:Scripted',
  nameWithTrademark: 'Life Re:Scripted™',
  tagline: 'Reclaim. Rebuild. Reignite.',

  // SEO and meta content
  seo: {
    title: 'Life Re:Scripted™ - Reclaim. Rebuild. Reignite.',
    description:
      'Life Re:Scripted is a structured personal transformation platform helping people rebuild identity, habits, and direction through journaling, habit systems, reflection, and AI-supported tools.',
    keywords:
      'personal transformation platform, identity rebuilding, habit systems, structured reflection, AI-supported journaling, personal growth, life transitions',
    author: 'Life Re:Scripted Team',

    // OpenGraph / Social Media
    ogTitle: 'Life Re:Scripted™ - Reclaim. Rebuild. Reignite.',
    ogDescription:
      'A structured personal transformation platform for rebuilding identity, habits, and direction through journaling, habit systems, reflection, and AI-supported tools.',
    ogImage: '/images/og-image.jpg', // You'll want to create this
    twitterCard: 'summary_large_image',
    twitterSite: '@Life_ReScripted',
  },

  // Founder identity (canonical SEO source-of-truth for Person schema and bios)
  founder: {
    name: 'Michael David Connell',
    firstName: 'Michael',
    title: 'Founder & CEO',
    shortBio:
      'Michael is the Founder & CEO of Life Re:Scripted, a structured personal transformation platform designed to help people rebuild identity, habits, and direction through reflection, execution systems, and AI-supported tools.',
    longBio:
      'After navigating major life transitions, career change, and personal setbacks, Michael developed Life Re:Scripted as a practical operating system for rebuilding. The platform supports veterans, professionals in transition, and people rebuilding after burnout through journaling, habit systems, guided reflection, and AI-supported tools.',
    schemaDescription:
      'Founder of Life Re:Scripted, a structured personal transformation platform focused on identity rebuilding, habit systems, reflection, and AI-supported journaling tools.',
    social: {
      linkedin: 'https://www.linkedin.com/in/michaeldavidconnell/',
      facebook: 'https://www.facebook.com/profile.php?id=61576770173551',
    },
  },

  // Organization / legal entity
  organization: {
    legalName: 'Life Re:Scripted, Inc.',
    url: 'https://liferescripted.net',
    twitter: 'https://x.com/Life_ReScripted',
    foundingDate: '2024',
    description:
      'Life Re:Scripted is a structured personal transformation platform helping people rebuild identity, habits, and direction through journaling, habit systems, reflection, and AI-supported tools.',
  },

  // Mission and values
  mission: {
    tagline: 'Reclaim. Rebuild. Reignite.',
    shortDescription:
      'From ashes to triumph. From setback to comeback. Your transformation starts here.',
    longDescription:
      'Life Re:Scripted™ is where warriors document their transformation, track their progress, and rise stronger than before.',
    builtBy: 'Built by a veteran who knows the journey of rising from ashes',
    footer: 'Built by a disabled veteran who knows the journey',
  },

  // Copyright and legal
  legal: {
    copyright: `© ${new Date().getFullYear()} Life Re:Scripted™`,
    fullCopyright: `© ${new Date().getFullYear()} Life Re:Scripted™. Built by a veteran who knows the journey of rising from ashes.`,
    taglineStatement: 'Reclaim. Rebuild. Reignite.',
    privacyStatement: 'Veterans built. Privacy protected. Stories honored.',
  },
};

// Helper functions for different display contexts
export const getBrandName = (withTrademark = false) => {
  return withTrademark ? BRAND_CONFIG.nameWithTrademark : BRAND_CONFIG.name;
};

export const getPageTitle = (pageTitle = null) => {
  if (pageTitle) {
    return `${pageTitle} - ${BRAND_CONFIG.nameWithTrademark}`;
  }
  return BRAND_CONFIG.seo.title;
};

export const getBrandLockup = (
  options: {
    includeTrademark?: boolean;
    includeTagline?: boolean;
    separator?: string;
  } = {}
) => {
  const { includeTrademark = true, includeTagline = true, separator = ' - ' } = options;

  const name = includeTrademark ? BRAND_CONFIG.nameWithTrademark : BRAND_CONFIG.name;

  if (includeTagline) {
    return `${name}${separator}${BRAND_CONFIG.tagline}`;
  }

  return name;
};

// Structured data for SEO
export const getStructuredData = () => {
  return {
    '@context': 'https://schema.org',
    '@type': 'WebApplication',
    name: BRAND_CONFIG.nameWithTrademark,
    description: BRAND_CONFIG.seo.description,
    url: 'https://liferescripted.net',
    applicationCategory: 'HealthApplication',
    operatingSystem: 'Web Browser',
    offers: {
      '@type': 'Offer',
      price: '0',
      priceCurrency: 'USD',
    },
  };
};

// Export commonly used combinations
export const BRAND_LOCKUP_FULL = getBrandLockup({ includeTrademark: true, includeTagline: true });
export const BRAND_NAME_TM = getBrandName(true);
export const BRAND_NAME_CLEAN = getBrandName(false);
export const TAGLINE = BRAND_CONFIG.tagline;

export default BRAND_CONFIG;
